import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProviders, createProvider, deleteProvider } from '../services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Trash2, Users } from 'lucide-react';

const ProvidersPage = () => {
  const { isAdmin } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [name, setName] = useState('');
  const [area, setArea] = useState('');
  const [limit, setLimit] = useState('20');
  const [birthday, setBirthday] = useState('');

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const data = await getProviders();
      setProviders(data);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async () => {
    if (!name.trim() || !limit) {
      alert('Preencha corretamente.');
      return;
    }
    
    try {
      await createProvider({
        name: name.trim(),
        area: area.trim() || 'Geral',
        limit: parseInt(limit),
        birthday: birthday || null
      });
      
      // Reset form
      setName('');
      setArea('');
      setLimit('20');
      setBirthday('');
      
      loadProviders();
      alert('Parceiro adicionado!');
    } catch (error) {
      alert('Erro ao adicionar parceiro');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Isso removerá o parceiro e TODOS os dados dele.')) {
      try {
        await deleteProvider(id);
        loadProviders();
      } catch (error) {
        alert('Erro ao remover parceiro');
      }
    }
  };

  const formatBirthday = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-in">
      <header className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-slate-900">Gerenciar Parceiros</h2>
        <p className="text-slate-500 text-sm">Cadastro de parceiros e definições de disponibilidade.</p>
      </header>

      {/* Add Provider Form - Only for Admin */}
      {isAdmin() && (
        <Card className="border-slate-200" data-testid="add-provider-form">
          <CardContent className="p-6 flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label>Nome</Label>
              <Input
                placeholder="Ex: João da Silva"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="border-slate-300"
                data-testid="provider-name"
              />
            </div>
            <div className="w-40">
              <Label>Área/Atuação</Label>
              <Input
                placeholder="Ex: Dev"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className="border-slate-300"
                data-testid="provider-area"
              />
            </div>
            <div className="w-40">
              <Label>Aniversário</Label>
              <Input
                type="date"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                className="border-slate-300"
                data-testid="provider-birthday"
              />
              <p className="text-[10px] text-slate-400 mt-1">Para Flexibilidade</p>
            </div>
            <div className="w-24">
              <Label>Limite</Label>
              <Input
                type="number"
                placeholder="20"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="border-slate-300"
                data-testid="provider-limit"
              />
            </div>
            <Button 
              onClick={handleAddProvider}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-provider-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" /> Add
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Provider List */}
      <Card className="border-slate-200" data-testid="providers-list">
        <CardContent className="p-0">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 font-medium uppercase text-xs tracking-wide">
              <tr>
                <th className="px-6 py-3">Nome</th>
                <th className="px-6 py-3">Área</th>
                <th className="px-6 py-3 text-center">Data Especial</th>
                <th className="px-6 py-3 text-center">Limite</th>
                <th className="px-6 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {providers.map(p => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`provider-item-${p.id}`}>
                  <td className="px-6 py-4 font-medium text-slate-900">{p.name}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">{p.area}</td>
                  <td className="px-6 py-4 text-sm text-slate-500 text-center">{formatBirthday(p.birthday)}</td>
                  <td className="px-6 py-4 text-center">{p.limit} dias</td>
                  <td className="px-6 py-4 text-right">
                    {isAdmin() && (
                      <button 
                        onClick={() => handleDelete(p.id)}
                        className="text-slate-400 hover:text-red-500 transition flex items-center gap-1 ml-auto text-xs font-medium"
                        data-testid={`delete-provider-${p.id}`}
                      >
                        <Trash2 className="h-4 w-4" /> Remover
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {providers.length === 0 && (
            <div className="p-8 text-center text-slate-400">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Nenhum parceiro cadastrado.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProvidersPage;
